import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { concatMap, filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  RemoteDiagnosticsCloudApiPicker,
  RemoteDiagnosticsResponse,
  RemoteDiagnosticsStatus,
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVddGenericStructure,
} from '@vi-iot/client';

import { RemoteDiagnosticsChartValue, RemoteDiagnosticsStore } from '@store';

@Injectable({ providedIn: 'root' })
export class DiagnosticsWebsocketService<T> {
  public ngUnsubscribe: Subject<void> = new Subject();
  public webSocketValues: Subject<RemoteDiagnosticsResponse<T>> = new Subject();

  constructor(
    private remoteDiagnosticsCloudApiPicker: RemoteDiagnosticsCloudApiPicker,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore
  ) {}

  initializeWebsocket(gatewaySerial: string): void {
    this.remoteDiagnosticsStore
      .getXAuthToken(gatewaySerial)
      .pipe(
        withLatestFrom(this.remoteDiagnosticsStore.getDiagStackNode(gatewaySerial)),
        take(1),
        concatMap(([token, hostAndPath]) =>
          this.remoteDiagnosticsCloudApiPicker
            .instance()
            .connectResponseWebSocketSubject(hostAndPath, token)
            .pipe(
              takeUntil(this.ngUnsubscribe),
              map((wsRawResponse) => this.extractMessageFromWebsocketResponse(wsRawResponse.data)),
              filter((wsData) => wsData && wsData.status === RemoteDiagnosticsStatus.ENDED)
            )
        )
      )
      .subscribe((values) => {
        this.webSocketValues.next(values);
      });
  }

  extractMessageFromWebsocketResponse(
    websocketResponse: string
  ): RemoteDiagnosticsResponse<T> | null {
    if (websocketResponse && websocketResponse.indexOf('MESSAGE')) {
      const startPosition = websocketResponse.indexOf('{');
      const endPosition = websocketResponse.lastIndexOf('}');

      if (startPosition !== -1 && endPosition !== -1) {
        const stringObject =
          '"' + websocketResponse.substring(startPosition, endPosition + 1) + '"';
        const parsedObject = this.tryParseJSON(stringObject);

        return parsedObject as RemoteDiagnosticsResponse<T>;
      }
    }

    return null;
  }

  extractSensorValuesFromWebsocketResponse(
    websocketResponse: RemoteDiagnosticsResponse<T>
  ): RemoteDiagnosticsChartValue {
    const websocketData = (websocketResponse.data as unknown) as RemoteDiagnosticsSuccessResponseData;
    const structure = websocketData.structure
      .find(
        (dataStructure: RemoteDiagnosticsVddGenericStructure) =>
          dataStructure.path === 'PA_DataRecor'
      )
      .structure.find((dataStructure) => dataStructure.path === 'PA_DataRecor/PA_Value');
    const value = structure?.value;
    const unit = structure?.unit;

    const chartValue: RemoteDiagnosticsChartValue = {
      requestId: websocketResponse.requestId,
      wsTime: new Date(this.normalizeDateFormatToIso8601(websocketResponse.date)).getTime(),
      value: +value,
      unit,
    };

    return chartValue;
  }

  /*
  We receive date in format like   '2021-01-04 11:05:59.559'
  In Safari, new Date("2021-01-04 11:05:59.559") returns Invalid Date
  So basically i convert format from:
  '2021-01-04 11:05:59.559' to:
  '2021-01-04T11:05:59.559'
  Which seems to be working in all browsers. ISO 8601
  */
  normalizeDateFormatToIso8601(diagStackDateString: string): string {
    return diagStackDateString.replace(' ', 'T').concat('Z');
  }

  private tryParseJSON(jsonString: string): unknown | null {
    try {
      const o = JSON.parse(jsonString);
      const o2 = JSON.parse(o);
      if (o2 && typeof o2 === 'object') {
        return o2;
      }
    } catch {}

    return null;
  }
}
